import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ExternalLink, RefreshCw, Cloud, X, Image as ImageIcon } from "lucide-react";
import { useAdminAuth, LoginScreen } from "./adminAuth";
import AdminHeader from "../components/AdminHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NEXTCLOUD_SHARE_URL = "https://cloud.nhaage.de/index.php/s/dxs2C3AMqtFKr5D";

export default function AdminGallery() {
  const { authed, checking, login, logout } = useAdminAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gallery`);
      setPhotos(res.data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (checking) return <div className="min-h-screen bg-sage-50" />;
  if (!authed) return <LoginScreen onSubmit={login} />;

  return (
    <div data-testid="admin-gallery" className="min-h-screen bg-sage-50">
      <AdminHeader onLogout={logout} />

      <main className="container mx-auto px-6 md:px-12 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl text-sage-800 flex items-center gap-3">
              <Cloud size={26} className="text-sage-500" />
              Galerie · Nextcloud
            </h2>
            <p className="text-sm text-sage-600 mt-1">
              Alle Fotos liegen in deiner Nextcloud (
              <a
                href={NEXTCLOUD_SHARE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-sage-800"
              >
                cloud.nhaage.de
              </a>
              ). Gäste können direkt von der Hochzeitsseite Fotos hochladen.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-gallery-refresh"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-sage-300 text-sage-700 hover:bg-white px-4 py-2 text-xs uppercase tracking-wide"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Aktualisieren
            </button>
            <a
              href={NEXTCLOUD_SHARE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 text-xs uppercase tracking-wide"
            >
              <ExternalLink size={14} />
              In Nextcloud öffnen
            </a>
          </div>
        </div>

        <div className="bg-sage-100/60 border border-sage-200 rounded-xl p-4 mb-8 text-sm text-sage-700">
          <strong>Hinweis:</strong> Zum <strong>Löschen</strong> eines Fotos öffne den Nextcloud-Ordner
          („In Nextcloud öffnen") — aus Sicherheitsgründen sind Public-Share-Links nicht zum Löschen
          berechtigt.
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-sage-800">
            Aktuell in der Galerie{" "}
            <span className="text-sage-500 text-sm">({photos.length})</span>
          </h3>
        </div>

        {photos.length === 0 ? (
          <div className="bg-white border border-sage-200 rounded-2xl p-16 text-center text-sage-500">
            <ImageIcon className="mx-auto mb-3 text-sage-300" size={32} />
            <p>Noch keine Fotos in der Nextcloud-Galerie.</p>
          </div>
        ) : (
          <div
            data-testid="admin-gallery-grid"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          >
            {photos.map((p, i) => (
              <button
                key={p.name}
                data-testid={`admin-photo-${i}`}
                onClick={() => setLightbox(p)}
                className="group relative aspect-square rounded-xl overflow-hidden bg-sage-100 shadow-sm text-left"
              >
                <img
                  src={p.thumb_url}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-sage-950/85 to-transparent">
                  <p className="text-white text-xs truncate font-medium">{p.name}</p>
                  <p className="text-white/70 text-[10px]">
                    {(p.size / 1024 / 1024).toFixed(1)} MB
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-sage-950/90 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2 bg-white/10 rounded-full"
            onClick={() => setLightbox(null)}
            aria-label="Schließen"
          >
            <X size={24} />
          </button>
          <img
            src={lightbox.full_url}
            alt={lightbox.name}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
